// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { authApi } from '../api/auth';
import type { GlobalRole, Group, GroupCase, GroupMember } from '../api/types';
import { canManageGroups, globalRoleOf, normalizeMemberEmail, ROLE_LABELS, SHARE_ROLE_LABELS } from '../app/auth';
import { useAuth } from '../app/AuthGate';
import { caseHref } from '../app/cases';
import { useArmedConfirm } from '../app/useArmedConfirm';
import { useAsync } from '../app/useAsync';
import { useMutationQueue } from '../app/useMutationQueue';
import { Button } from '../components/Button';
import { DataTable, type Column } from '../components/DataTable';
import { ErrorBanner } from '../components/ErrorBanner';
import { Spinner } from '../components/Spinner';
import { TextInput } from '../components/TextInput';
import controls from '../components/controls.module.css';
import { GroupCaseList } from '../components/groups/GroupCaseList';
import { GroupList } from '../components/groups/GroupList';
import {
  canSeeGroupCases,
  caseCountText,
  GROUP_NAME_MAX,
  groupErrorText,
  groupNameCheck,
  memberCountText,
  memberName,
  selectGroup,
  sortGroupMembers,
  sortGroups,
  type GroupAction,
} from '../groups/model';
import styles from './GroupsPage.module.css';

const INTRO = 'Grupper samler kolleger, så en sag kan deles med dem alle på én gang.';

/**
 * Groups (ruxd-wmc.2): every group on the server, its members and the cases
 * shared with it. A group is flat — no leader, no roles inside it. Group
 * managers and admins (the global role) create, rename and delete groups and
 * add or remove members; anyone may leave a group they are in. Local mode
 * has no groups.
 */
export function GroupsPage() {
  const auth = useAuth();
  const me = auth?.me;
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Grupper</h1>
        <span className={styles.sub}>{INTRO}</span>
      </header>
      {me?.mode === 'server' ? (
        <GroupsWorkspace myId={me.user.id} globalRole={globalRoleOf(me.user)} />
      ) : (
        <section className={`${styles.panel} ${styles.lone}`} aria-label="Grupper">
          <p className={styles.text}>
            Serveren kører i lokal tilstand: én person, intet login og ingen grupper. Start ruxd uden{' '}
            <code className={styles.code}>--local</code> for brugere, grupper og deling.
          </p>
        </section>
      )}
    </div>
  );
}

function GroupsWorkspace({ myId, globalRole }: { myId: string; globalRole: GlobalRole }) {
  const manage = canManageGroups(globalRole);
  const groups = useAsync<Group[]>((s) => authApi.groups(s), []);
  const [params, setParams] = useSearchParams();
  const listId = useId();

  if (groups.error && !groups.data)
    return <ErrorBanner error={groups.error} onRetry={groups.reload} context="grupper" />;
  if (!groups.data) return <Spinner label="Indlæser grupper…" />;

  const list = sortGroups(groups.data);
  const selectedId = selectGroup(list, params.get('gruppe'));
  const selected = list.find((g) => g.id === selectedId) ?? null;
  const select = (id: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set('gruppe', id);
        return next;
      },
      { replace: true },
    );

  return (
    <div className={styles.layout}>
      <section className={`${styles.panel} ${styles.aside}`} aria-labelledby={listId}>
        <div className={styles.panelHead}>
          <h2 id={listId} className={styles.panelHeading}>
            Alle grupper
          </h2>
          <span className={styles.count}>{list.length}</span>
        </div>
        <GroupList
          label="Alle grupper"
          selectedId={selectedId}
          onSelect={select}
          items={list.map((g) => ({
            id: g.id,
            name: g.name,
            detail: memberCountText(g.member_count),
            badge: g.is_member ? 'Du er med' : undefined,
          }))}
          empty={
            <p className={styles.muted}>
              {manage
                ? 'Der er ingen grupper endnu. Opret den første herunder.'
                : 'Der er ingen grupper endnu. En gruppeadministrator opretter dem.'}
            </p>
          }
        />
        {manage && (
          <CreateGroup
            onCreated={(group) => {
              select(group.id);
              groups.reload();
            }}
          />
        )}
      </section>

      {selected ? (
        <GroupDetail
          key={selected.id}
          group={selected}
          myId={myId}
          globalRole={globalRole}
          onChanged={groups.reload}
        />
      ) : (
        list.length > 0 && <p className={styles.muted}>Vælg en gruppe.</p>
      )}
    </div>
  );
}

function CreateGroup({ onCreated }: { onCreated: (group: Group) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const check = groupNameCheck(name);
    if (!check.ok) {
      setFailure(check.reason);
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const group = await authApi.createGroup(check.value);
      setName('');
      onCreated(group);
    } catch (cause) {
      setFailure(groupErrorText(cause, 'create'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.create} onSubmit={(e) => void submit(e)}>
      <label className={controls.field}>
        <span className={controls.fieldLabel}>Ny gruppe</span>
        <TextInput
          variant="outline"
          size="sm"
          block
          value={name}
          maxLength={GROUP_NAME_MAX}
          placeholder="Fx Miljøkortlægning Aarhus"
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <Button type="submit" variant="primary" size="sm" disabled={busy || name.trim() === ''}>
        {busy ? 'Opretter…' : 'Opret gruppe'}
      </Button>
      {failure && (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
    </form>
  );
}

/** What the armed confirm is armed on: a member's removal, leaving, or the group itself. */
type Armed = { kind: 'remove'; uid: string } | { kind: 'delete' } | { kind: 'leave' };
const armedKey = (a: Armed) => (a.kind === 'remove' ? `remove:${a.uid}` : a.kind);

function GroupDetail({
  group,
  myId,
  globalRole,
  onChanged,
}: {
  group: Group;
  myId: string;
  globalRole: GlobalRole;
  onChanged: () => void;
}) {
  const membersId = useId();
  const casesId = useId();
  const manage = canManageGroups(globalRole);
  const seeCases = canSeeGroupCases(group, globalRole);
  const members = useAsync<GroupMember[]>((s) => authApi.groupMembers(group.id, s), [group.id]);
  // Outsiders who cannot run groups get a 403 here, so they are not asked.
  const cases = useAsync<GroupCase[] | null>(
    (s) => (seeCases ? authApi.groupCases(group.id, s) : Promise.resolve(null)),
    [group.id, seeCases],
  );
  const [email, setEmail] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(group.name);
  const [failure, setFailure] = useState<string | null>(null);
  const action = useRef<GroupAction>('add');

  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    ignoreCaseRole: true,
    onError: (cause) => setFailure(groupErrorText(cause, action.current)),
    onSettled: () => {
      // A deleted group drops out of the list; re-reading its members would only 404.
      if (action.current !== 'delete') members.reload();
      onChanged();
    },
  });
  const confirm = useArmedConfirm<string>(busy, members.data);

  if (members.error && !members.data)
    return (
      <section className={styles.panel}>
        <p className={styles.error} role="alert">
          {groupErrorText(members.error, 'load')}
        </p>
        <div>
          <Button variant="outline" size="sm" onClick={members.reload}>
            Prøv igen
          </Button>
        </div>
      </section>
    );
  if (!members.data) return <Spinner label="Indlæser gruppe…" />;

  const list = sortGroupMembers(members.data);
  const run = (kind: GroupAction, work: () => Promise<void>) => {
    setFailure(null);
    confirm.disarm();
    action.current = kind;
    void mutate(work);
  };

  const rename = (e: FormEvent) => {
    e.preventDefault();
    const check = groupNameCheck(draft, group.name);
    if (!check.ok) {
      setFailure(check.reason);
      return;
    }
    run('rename', async () => {
      await authApi.renameGroup(group.id, check.value);
      setRenaming(false);
    });
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    const address = normalizeMemberEmail(email);
    if (!address) {
      setFailure('Skriv en gyldig e-mail.');
      return;
    }
    run('add', async () => {
      await authApi.addGroupMember(group.id, address);
      setEmail('');
    });
  };

  const armButton = (armed: Armed, label: string, confirmLabel: string, onConfirm: () => void, ghost = true) =>
    confirm.armed === armedKey(armed) ? (
      <Button
        variant="outline"
        tone="danger"
        size="sm"
        armed
        disabled={busy}
        onClick={onConfirm}
        onBlur={confirm.disarm}
      >
        {confirmLabel}
      </Button>
    ) : (
      <Button
        variant="outline"
        tone={ghost ? 'neutral' : 'danger'}
        size="sm"
        disabled={busy}
        onClick={(e) => confirm.arm(armedKey(armed), e.currentTarget)}
      >
        {label}
      </Button>
    );

  const columns: Column<GroupMember>[] = [
    {
      key: 'member',
      header: 'Medlem',
      render: (m) => (
        <span className={styles.who}>
          <span className={styles.name}>
            {memberName(m)}
            {m.user.id === myId && <span className={styles.you}>dig</span>}
          </span>
          {m.user.display_name && <span className={`${styles.email} mono`}>{m.user.email}</span>}
        </span>
      ),
    },
    {
      key: 'action',
      header: '',
      render: (m) => {
        // Your own row: leave (no roles in a group, so nothing stops it). Others: managers remove.
        const button =
          m.user.id === myId
            ? armButton({ kind: 'leave' }, 'Forlad gruppe', 'Forlad gruppen?', () =>
                run('leave', async () => {
                  await authApi.removeGroupMember(group.id, myId);
                }),
              )
            : manage
              ? armButton({ kind: 'remove', uid: m.user.id }, 'Fjern', `Fjern ${memberName(m)}?`, () =>
                  run('remove', async () => {
                    await authApi.removeGroupMember(group.id, m.user.id);
                  }),
                )
              : null;
        return button && <span className={styles.end}>{button}</span>;
      },
    },
  ];

  return (
    <div className={styles.detail}>
      <section className={styles.panel} aria-label={group.name}>
        <div className={styles.groupHead}>
          {renaming ? (
            <form className={styles.renameForm} onSubmit={rename}>
              <label className={controls.field}>
                <span className={controls.fieldLabel}>Gruppens navn</span>
                <TextInput
                  variant="outline"
                  size="sm"
                  block
                  value={draft}
                  maxLength={GROUP_NAME_MAX}
                  autoFocus
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setRenaming(false);
                  }}
                />
              </label>
              <Button type="submit" variant="primary" size="sm" disabled={busy}>
                Gem navn
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRenaming(false)}>
                Annullér
              </Button>
            </form>
          ) : (
            <h2 className={styles.groupName}>{group.name}</h2>
          )}
          {!renaming && manage && (
            <div className={styles.actions}>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setDraft(group.name);
                  setFailure(null);
                  setRenaming(true);
                }}
              >
                Omdøb
              </Button>
              {armButton(
                { kind: 'delete' },
                'Slet gruppe',
                `Slet ${group.name}?`,
                () =>
                  run('delete', async () => {
                    await authApi.deleteGroup(group.id);
                  }),
                false,
              )}
            </div>
          )}
        </div>
        <p className={styles.facts}>
          <span>{memberCountText(list.length)}</span>
          {cases.data && <span>{caseCountText(cases.data.length)}</span>}
          <span>{group.is_member ? 'Du er med i gruppen' : 'Du er ikke med i gruppen'}</span>
        </p>
        {confirm.armed === 'delete' && (
          <p className={styles.notice}>
            Gruppen slettes med alle dens delinger. Medlemmer, der kun så en sag gennem gruppen, mister adgangen til
            den.
          </p>
        )}
        {failure && (
          <p className={styles.error} role="alert">
            {failure}
          </p>
        )}
      </section>

      <section className={styles.panel} aria-labelledby={membersId}>
        <div className={styles.panelHead}>
          <h2 id={membersId} className={styles.panelHeading}>
            Medlemmer
          </h2>
          <span className={styles.count}>{list.length}</span>
        </div>
        <DataTable
          columns={columns}
          rows={list}
          rowKey={(m) => m.user.id}
          empty={<p className={styles.muted}>Gruppen har ingen medlemmer endnu.</p>}
        />
        {manage ? (
          <form className={styles.row} onSubmit={add}>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>Tilføj med e-mail</span>
              <TextInput
                variant="outline"
                size="sm"
                block
                type="email"
                value={email}
                placeholder="navn@firma.dk"
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <Button type="submit" variant="primary" size="sm" disabled={busy || email.trim() === ''}>
              Tilføj
            </Button>
          </form>
        ) : (
          <p className={styles.muted}>Gruppeadministratorer tilføjer og fjerner medlemmer.</p>
        )}
      </section>

      <section className={styles.panel} aria-labelledby={casesId}>
        <div className={styles.panelHead}>
          <h2 id={casesId} className={styles.panelHeading}>
            Delte sager
          </h2>
          {cases.data && <span className={styles.count}>{cases.data.length}</span>}
        </div>
        {!seeCases ? (
          <p className={styles.muted}>Kun gruppens medlemmer kan se, hvilke sager der er delt med den.</p>
        ) : cases.error && !cases.data ? (
          <div className={styles.retry}>
            <p className={styles.error} role="alert">
              {groupErrorText(cases.error, 'cases')}
            </p>
            <Button variant="outline" size="sm" onClick={cases.reload}>
              Prøv igen
            </Button>
          </div>
        ) : !cases.data ? (
          <Spinner label="Indlæser sager…" />
        ) : (
          <GroupCaseList
            label={`Sager delt med ${group.name}`}
            items={cases.data.map((c) => ({
              id: c.id,
              name: c.name,
              href: caseHref(c.id),
              share: SHARE_ROLE_LABELS[c.role],
              mine: c.my_role ? ROLE_LABELS[c.my_role] : undefined,
            }))}
            empty={
              <p className={styles.muted}>
                Ingen sager er delt med gruppen endnu. En sags ejer deler den med gruppen fra sagens indstillinger.
              </p>
            }
          />
        )}
      </section>
    </div>
  );
}
